import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Filter state that lives in the URL query string, so a filtered view can be
 * bookmarked, reloaded, or copied and sent to someone else, and back/forward
 * walk through filter changes.
 *
 * The URL is the only source of truth: values are parsed from it on every
 * change and setters write back to it. Params not in the schema (tabs, drawer
 * ids, ...) are left alone. A value equal to its default is dropped from the
 * URL to keep links short.
 *
 * Define the schema OUTSIDE the component (or memoise it) so it is stable:
 *
 *   const FILTERS = {
 *     search:  { type: 'string' },
 *     status:  { type: 'array' },                       // ?status=A&status=B
 *     account: { type: 'string', default: 'all' },
 *     page:    { type: 'number', default: 1 },
 *     created: { type: 'dateRange', keys: ['created_after', 'created_before'] },
 *     since:   { type: 'datetime', key: 'since' },
 *     archived:{ type: 'boolean', default: false },
 *   };
 *   const [filters, setFilters] = useUrlFilters(FILTERS);
 *   setFilters({ status: ['PAID'], page: 1 });
 *
 * Types:
 *   string    -> string | default (null if none)
 *   number    -> number | default
 *   boolean   -> true/false | default
 *   array     -> string[] (repeated param), default []
 *   date      -> dayjs | null, written as YYYY-MM-DD
 *   datetime  -> dayjs | null, written as ISO-8601 (UTC)
 *   dateRange -> [dayjs, dayjs] | null over two params (`keys`), written as
 *                YYYY-MM-DD, or ISO-8601 with `withTime: true`
 *
 * `key` overrides the URL param name (defaults to the schema key).
 *
 * Options:
 *   prefix  - prepended to every URL param name. Use it when the component is
 *             embedded in another page that has its own filters (a tab on a
 *             detail page), e.g. `{ prefix: 'inv_' }` -> ?inv_status=PAID.
 *   enabled - false keeps the filters in local state instead of the URL, for
 *             the same component rendered inside a modal/drawer/picker.
 */

const isEmpty = (value) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const defaultFor = (spec) => {
  if (spec.default !== undefined) return spec.default;
  if (spec.type === 'array') return [];
  return null;
};

const parseDate = (raw) => {
  if (!raw) return null;
  const value = dayjs(raw);
  return value.isValid() ? value : null;
};

const formatDate = (value, withTime) => {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return withTime ? d.toISOString() : d.format('YYYY-MM-DD');
};

function parseOne(params, name, spec) {
  const paramKey = spec.key || name;
  switch (spec.type) {
    case 'array': {
      const values = params.getAll(paramKey).filter((v) => v !== '');
      return values.length ? values : defaultFor(spec);
    }
    case 'number': {
      const raw = params.get(paramKey);
      const value = raw === null || raw === '' ? NaN : Number(raw);
      return Number.isFinite(value) ? value : defaultFor(spec);
    }
    case 'boolean': {
      const raw = params.get(paramKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return defaultFor(spec);
    }
    case 'date':
    case 'datetime':
      return parseDate(params.get(paramKey)) || defaultFor(spec);
    case 'dateRange': {
      const [startKey, endKey] = spec.keys;
      const start = parseDate(params.get(startKey));
      const end = parseDate(params.get(endKey));
      return start && end ? [start, end] : defaultFor(spec);
    }
    default: {
      const raw = params.get(paramKey);
      return raw === null || raw === '' ? defaultFor(spec) : raw;
    }
  }
}

const sameAsDefault = (spec, value) => {
  const def = defaultFor(spec);
  if (def === null || def === undefined) return false;
  if (Array.isArray(def)) {
    return Array.isArray(value) && value.length === def.length && value.every((v, i) => String(v) === String(def[i]));
  }
  return String(value) === String(def);
};

function writeOne(params, name, spec, value) {
  if (spec.type === 'dateRange') {
    const [startKey, endKey] = spec.keys;
    params.delete(startKey);
    params.delete(endKey);
    const start = formatDate(value?.[0], spec.withTime);
    const end = formatDate(value?.[1], spec.withTime);
    if (start && end) {
      params.set(startKey, start);
      params.set(endKey, end);
    }
    return;
  }

  const paramKey = spec.key || name;
  params.delete(paramKey);
  if (isEmpty(value) || sameAsDefault(spec, value)) return;

  switch (spec.type) {
    case 'array':
      value.forEach((v) => {
        if (!isEmpty(v)) params.append(paramKey, String(v));
      });
      break;
    case 'date':
    case 'datetime': {
      const formatted = formatDate(value, spec.type === 'datetime');
      if (formatted) params.set(paramKey, formatted);
      break;
    }
    default:
      params.set(paramKey, String(value));
  }
}

const withPrefix = (schema, prefix) => {
  if (!prefix) return schema;
  return Object.fromEntries(Object.entries(schema).map(([name, spec]) => [name, {
    ...spec,
    key: `${prefix}${spec.key || name}`,
    ...(spec.keys ? { keys: spec.keys.map((k) => `${prefix}${k}`) } : {}),
  }]));
};

export default function useUrlFilters(rawSchema, { prefix = '', enabled = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // With `enabled: false` the same parse/write logic runs against a private
  // query string held in state instead of the location.
  const [localQuery, setLocalQuery] = useState('');
  const query = enabled ? searchParams.toString() : localQuery;
  const schema = useMemo(() => withPrefix(rawSchema, prefix), [rawSchema, prefix]);

  // react-router's functional setSearchParams hands every call the params of
  // the last render, so two updates in one tick (e.g. set search, then reset
  // page) would clobber each other. Track the pending query ourselves and
  // resync whenever the real URL moves.
  const pending = useRef({ query, params: new URLSearchParams(query) });
  if (pending.current.query !== query) {
    pending.current = { query, params: new URLSearchParams(query) };
  }

  // Re-parsed only when the query string changes, so dayjs/array values keep
  // their identity between renders and are safe as effect dependencies.
  const filters = useMemo(() => {
    const params = new URLSearchParams(query);
    return Object.fromEntries(
      Object.entries(schema).map(([name, spec]) => [name, parseOne(params, name, spec)])
    );
  }, [query, schema]);

  /**
   * Merge `updates` ({ name: value }) into the URL. `null`/`[]`/'' clears a
   * filter. Pass `{ replace: false }` to add a history entry (default replaces,
   * so typing in a filter does not flood the back button).
   */
  const setFilters = useCallback((updates, { replace = true } = {}) => {
    const next = new URLSearchParams(pending.current.params);
    Object.entries(updates).forEach(([name, value]) => {
      const spec = schema[name];
      if (spec) writeOne(next, name, spec, value);
    });
    pending.current.params = next;
    if (enabled) {
      setSearchParams(next, { replace });
    } else {
      setLocalQuery(next.toString());
    }
  }, [schema, setSearchParams, enabled]);

  /** Reset every schema filter except the names in `keep`. */
  const clearFilters = useCallback((keep = []) => {
    const updates = {};
    Object.keys(schema).forEach((name) => {
      if (!keep.includes(name)) updates[name] = null;
    });
    setFilters(updates);
  }, [schema, setFilters]);

  return [filters, setFilters, clearFilters];
}
